import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { cn } from "@/lib/utils"
import type { ChannelSeries } from "@/types/channel-analytics"

/**
 * OKS-734 — Channel growth chart (single-series area, switchable metric).
 *
 * Structural sibling of `EmvProgressionChart` (OKS-503): single series
 * plotted as an area, `--chart-1` token, `strokeWidth 2`,
 * `isAnimationActive={false}`, same a11y posture (`role="img"` + summary
 * `aria-label` + sibling `sr-only` mirror table, one row/day).
 *
 * # Gap vs. zero (TRD KD-4 — the load-bearing behaviour)
 *
 * The series is a DENSE per-day axis; an absent day is `value: null`, a
 * genuine 0-count day is `value: 0`. `connectNulls={false}` makes a gap
 * BREAK the line while a real 0 renders a plotted point. We never coerce a
 * gap to 0. The mirror table shows an em-dash for a gap day so screen
 * readers can tell a gap from a real zero too.
 */
export interface ChannelGrowthChartProps {
  channel: ChannelSeries
  className?: string
}

const EM_DASH = "—"

export function ChannelGrowthChart({
  channel,
  className,
}: ChannelGrowthChartProps) {
  const { t } = useTranslation()
  const metricLabel = t(`channel.filters.metric.${channel.metric}`)

  const config: ChartConfig = useMemo(
    () => ({
      value: {
        label: metricLabel,
        theme: { light: "var(--chart-1)", dark: "var(--chart-1)" },
      },
    }),
    [metricLabel],
  )

  // Present-day values (nulls excluded) for the aria summary min/max.
  const present = channel.series
    .map((p) => p.value)
    .filter((v): v is number => v !== null)
  const latest = present.length > 0 ? present[present.length - 1] : 0

  const ariaSummary = t("channel.chart.aria_summary", {
    metric: metricLabel,
    start: channel.series[0]?.date,
    end_date: channel.series[channel.series.length - 1]?.date,
    latest: formatCompactNumber(latest),
  })

  return (
    <Card
      data-testid="channel-growth-chart"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("channel.chart.title", { metric: metricLabel })}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        <div
          data-testid="channel-growth-chart-canvas"
          role="img"
          aria-label={ariaSummary}
        >
          <ChartContainer
            config={config}
            className="aspect-auto h-[200px] w-full sm:h-[240px] lg:h-[260px]"
          >
            <AreaChart
              data={channel.series}
              margin={{ left: 4, right: 12, top: 8, bottom: 0 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={24}
                tickFormatter={(value: string) =>
                  new Date(value).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })
                }
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={48}
                tickFormatter={(value: number) => formatCompactNumber(value)}
              />
              <ChartTooltip content={<ChannelTooltip label={metricLabel} />} />
              <Area
                type="monotone"
                dataKey="value"
                // Gap = null → break the line. Never plot an absent day as 0.
                connectNulls={false}
                stroke="var(--color-value)"
                fill="var(--color-value)"
                fillOpacity={0.15}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ChartContainer>
        </div>
        <SrOnlyTable channel={channel} metricLabel={metricLabel} />
      </CardContent>
    </Card>
  )
}

/**
 * Custom tooltip — hovered day + exact metric value. A gap day (null)
 * reads as an em-dash so a break is legible on hover too.
 */
function ChannelTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: { payload: { date: string; value: number | null } }[]
  label: string
}) {
  if (!active || !payload?.length) return null

  const datum = payload[0].payload
  return (
    <div className="bg-background grid gap-1 rounded-lg border px-3 py-2 text-xs shadow-md">
      <span className="text-foreground font-medium">{datum.date}</span>
      <span className="text-muted-foreground">
        {label}:{" "}
        <span className="text-foreground font-medium">
          {datum.value === null
            ? EM_DASH
            : new Intl.NumberFormat().format(datum.value)}
        </span>
      </span>
    </div>
  )
}

/**
 * Sr-only mirror table — one row per day. Screen readers consume this
 * directly; it's also a stable Playwright selector (chart canvas testing
 * is fragile). A gap day renders an em-dash, distinct from a plotted 0.
 */
function SrOnlyTable({
  channel,
  metricLabel,
}: {
  channel: ChannelSeries
  metricLabel: string
}) {
  const { t } = useTranslation()
  return (
    <table className="sr-only" data-testid="channel-growth-chart-mirror-table">
      <caption>
        {t("channel.chart.mirror_table_caption", { metric: metricLabel })}
      </caption>
      <thead>
        <tr>
          <th>{t("channel.chart.mirror_table.date")}</th>
          <th>{metricLabel}</th>
        </tr>
      </thead>
      <tbody>
        {channel.series.map((p) => (
          <tr key={p.date}>
            <td>{p.date}</td>
            <td>
              {p.value === null
                ? EM_DASH
                : new Intl.NumberFormat().format(p.value)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
